import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { exportResultsToExcel, exportResultsToCsv } from '../../services/batchLookupService';

const ExportActions = ({ results = [], disabled = false }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportMode, setExportMode] = useState('enriched'); // 'enriched' or 'clinical_only'
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pendingFormat, setPendingFormat] = useState('xlsx'); // 'xlsx' or 'csv'

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openExportDialog = (format) => {
    setPendingFormat(format);
    setExportDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmExport = () => {
    if (pendingFormat === 'xlsx') {
      exportResultsToExcel(results, exportMode, 'MyEMR_Diagnosis_Lookup');
    } else {
      exportResultsToCsv(results, exportMode, 'MyEMR_Diagnosis_Lookup');
    }
    setExportDialogOpen(false);
  };

  const directQuickExport = (format) => {
    if (format === 'xlsx') {
      exportResultsToExcel(results, exportMode, 'MyEMR_Diagnosis_Lookup');
    } else {
      exportResultsToCsv(results, exportMode, 'MyEMR_Diagnosis_Lookup');
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <ButtonGroup variant="contained" color="success" disabled={disabled || results.length === 0}>
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={() => directQuickExport('xlsx')}
            sx={{
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '8px 0 0 8px',
              px: 2.5,
              py: 0.9,
            }}
          >
            {t('Export to Excel (.xlsx)')}
          </Button>
          <Button
            size="small"
            onClick={handleMenuClick}
            sx={{ borderRadius: '0 8px 8px 0', px: 1 }}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<InsertDriveFileIcon />}
          disabled={disabled || results.length === 0}
          onClick={() => directQuickExport('csv')}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
            py: 0.9,
          }}
        >
          {t('Export to CSV')}
        </Button>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => directQuickExport('xlsx')}>
          <TableChartIcon fontSize="small" sx={{ mr: 1.5, color: 'success.main' }} />
          {t('Quick Export: Excel (.xlsx)')}
        </MenuItem>
        <MenuItem onClick={() => directQuickExport('csv')}>
          <InsertDriveFileIcon fontSize="small" sx={{ mr: 1.5, color: 'primary.main' }} />
          {t('Quick Export: CSV (.csv)')}
        </MenuItem>
        <MenuItem onClick={() => openExportDialog('xlsx')}>
          <CheckCircleIcon fontSize="small" sx={{ mr: 1.5, color: 'action.active' }} />
          {t('Customized Export Options...')}
        </MenuItem>
      </Menu>

      {/* Customized Export Options Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('Export Diagnosis Lookup Results')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Choose how you would like the exported file to be structured:')}
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={exportMode}
              onChange={(e) => setExportMode(e.target.value)}
            >
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 1.5,
                  borderRadius: 2,
                  borderColor: exportMode === 'enriched' ? 'primary.main' : 'divider',
                  bgcolor: exportMode === 'enriched' ? 'action.hover' : 'background.paper',
                }}
              >
                <FormControlLabel
                  value="enriched"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t('Preserve Original Columns + Append Diagnoses (Recommended)')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('Keeps all columns from your imported Excel/CSV file and appends EMR_Primary_Diagnosis, EMR_Matched_Name, EMR_AMKA, and EMR_Match_Status.')}
                      </Typography>
                    </Box>
                  }
                />
              </Card>

              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: exportMode === 'clinical_only' ? 'primary.main' : 'divider',
                  bgcolor: exportMode === 'clinical_only' ? 'action.hover' : 'background.paper',
                }}
              >
                <FormControlLabel
                  value="clinical_only"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t('Standardized Clinical Summary Report')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('Generates a clean report containing Row #, Search Name, Matched Patient, Diagnosis, Latest Visit Diagnosis, AMKA, Age, and Phone.')}
                      </Typography>
                    </Box>
                  }
                />
              </Card>
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setExportDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmExport}
            startIcon={<FileDownloadIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            {t('Download {{format}} File', { format: pendingFormat.toUpperCase() })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportActions;
